import type { AriaAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../../../Button";
import type { IconName } from "../../../Icon";
import { Icon } from "../../../Icon";
import { getControlButtonClasses } from "./utils";

export interface ControlButtonProps extends AriaAttributes {
  /**
   * Icon to display in the button
   */
  icon: IconName;
  /**
   * Size of the icon in pixels
   * @default 24
   */
  iconSize?: number;
  /**
   * Click handler for the button
   */
  onPress?: () => void;
  /**
   * Whether the button is disabled
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Additional classes for the button container
   */
  className?: string;
  /**
   * Additional classes for the icon
   */
  iconClassName?: string;
  /**
   * Button variant
   * @default "secondary"
   */
  variant?: "primary" | "secondary";
  /**
   * Whether the button is in an active/selected state
   * When true, uses primary action colors
   * @default false
   */
  isActive?: boolean;
  /**
   * Accessible label for screen readers
   */
  ariaLabel?: string;
  /**
   * Icon weight (for phosphor icons)
   * @default "regular"
   */
  iconWeight?: "thin" | "light" | "regular" | "bold" | "fill" | "duotone";
}

/**
 * Standard button component for map controls with consistent styling
 *
 * This component provides a consistent button style across all map controls:
 * - 48x48px square button on mobile, 40x40px on desktop
 * - Background surface color with hover state
 * - Shadow and border
 * - Support for active/selected state
 * - Icon-only design
 *
 * @example
 * // Basic usage
 * <ControlButton
 *   icon="MagnifyingGlass"
 *   onPress={() => console.log('Search clicked')}
 *   ariaLabel="Search"
 * />
 *
 * @example
 * // Active state (e.g., for toggle buttons)
 * <ControlButton
 *   icon="ListDashes"
 *   isActive={listIsOpen}
 *   onPress={toggleList}
 *   ariaLabel="Toggle list view"
 * />
 *
 * @example
 * // Disabled state
 * <ControlButton
 *   icon="Spinner"
 *   isDisabled={true}
 *   iconClassName="animate-spin"
 *   ariaLabel="Loading..."
 * />
 */
export function ControlButton({
  icon,
  iconSize = 24,
  onPress,
  isDisabled = false,
  className,
  iconClassName = "text-text-primary",
  variant = "secondary",
  isActive = false,
  ariaLabel,
  iconWeight = "regular",
  ...ariaProps
}: ControlButtonProps) {
  const buttonClasses = twMerge(
    getControlButtonClasses(),
    isActive && "bg-action-brand hover:bg-action-brand-hover border-action-brand",
    className
  );

  const iconClasses = twMerge(iconClassName, isActive && "text-text-inverse");

  return (
    <Button
      {...ariaProps}
      className={buttonClasses}
      onPress={onPress}
      variant={variant}
      size="sm"
      isDisabled={isDisabled}
      aria-label={ariaLabel}
      aria-pressed={isActive || undefined}
      type="button"
    >
      <Icon name={icon} size={iconSize} className={iconClasses} weight={iconWeight} />
    </Button>
  );
}
